import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { accessRoutes } from "components/ApplicationRoutes/routes";
import { getMiniPlayerSelector } from "modules/MiniPlayer/selectors";
import { Helmet } from "react-helmet";
import { getDisplayArtistFromTrack, getJazzAlbumImageFromTrack, getTitleFromTrack, isTrackJazz } from "utils/metaDataTools";
import { getLangSelector } from "modules/Config/selectors";
import { PAGE_ORIGIN_PODCASTS } from "utils/nameVariables";

//This component contains the metadata elements and
//their properties passed as props where implemented.
//It also appends track title when music is playing
// Via the trackTitle state

const VialmaHelmet = (props) => {
  const {
    t,
    lang,
    title,
    route,
    routeSuffix,
    routeSuffixFr,
    queryParams,
    content: contentProp,
    xDefault,
    prerenderStatusCode,
    ogProps,
    children,
    showHrefLangFr = true,
    showHrefLangEn = true,
    noIndex = false,
  } = props;

  const content = contentProp?.trim() ? contentProp : t("META_DEFAULT_DESCRIPTION");
  const { ogImage, ogImageWidth = "400", ogImageHeight = "400", ogTitle, ogType, ogDescription } = ogProps || "";

  let twitterProps;
  if (ogProps) {
    twitterProps = {
      twitterCard: "summary",
      twitterSite: "@Vialma_",
      twitterTitle: ogTitle,
      twitterDescription: ogDescription,
      twitterImage: ogImage,
    };
  }
  const { twitterCard = "summary", twitterSite = "@Vialma_", twitterTitle, twitterDescription, twitterImage } = twitterProps || "";

  const [trackTitle, setTrackTitle] = useState("");
  const { miniPlayer } = props;
  const { playing, indexOfTrack, dataTrack } = miniPlayer;

  useEffect(() => {
    if (dataTrack && dataTrack.length) {
      //adding controls for the media session and trackinfo
      const { composerImage, pageOrigin } = miniPlayer;

      const isJazzTrack = isTrackJazz(dataTrack[indexOfTrack]);
      const isPodcast = pageOrigin === PAGE_ORIGIN_PODCASTS;
      const title = isPodcast ? dataTrack[indexOfTrack].name : getTitleFromTrack(dataTrack[indexOfTrack]);
      setTrackTitle(title);

      const displayArtist = getDisplayArtistFromTrack(dataTrack[indexOfTrack]);
      const displayName = isPodcast ? dataTrack[indexOfTrack].podcast_name : displayArtist && displayArtist.name;

      const displayImage = isJazzTrack ? getJazzAlbumImageFromTrack(dataTrack[indexOfTrack]) : displayArtist && displayArtist.picture;
      const image = isPodcast ? composerImage : displayImage;

      //setting the mediaSession data
      const { mediaSession } = navigator;
      if (mediaSession) {
        mediaSession.metadata = new window.MediaMetadata({
          title: title,
          artist: `Vialma - ${displayName}`,
          artwork: [{ src: image }],
        });
      }
    } else {
      setTrackTitle("");
    }
  }, [playing, miniPlayer, dataTrack, indexOfTrack]);

  const baseUrl = "https://www.vialma.com";
  const suffix = routeSuffix ? `/${routeSuffix}` : "";
  const suffixFr = routeSuffixFr ? `/${routeSuffixFr}` : suffix;
  const canonicalSuffix = lang === "en" ? suffix : suffixFr;
  const ogSuffix = lang === "en" ? suffix : suffixFr;
  const queryStr = queryParams ? `?${queryParams}` : "";

  const titleStr = title ? title : "Vialma";
  return (
    <Helmet>
      {children}

      {ogType && <meta property="og:type" content={ogType} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImageWidth && <meta property="og:image:width" content={ogImageWidth} />}
      {ogImageHeight && <meta property="og:image:height" content={ogImageHeight} />}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogProps && <meta property="og:url" content={`${baseUrl}${accessRoutes[lang][route]}${ogSuffix}${queryStr}`} />}

      {twitterProps && <meta name="twitter:card" content={twitterCard} />}
      {twitterProps && <meta name="twitter:site" content={twitterSite} />}
      {twitterTitle && <meta name="twitter:title" content={twitterTitle} />}
      {twitterDescription && <meta name="twitter:description" content={twitterDescription} />}
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}

      <title>{trackTitle ? `${titleStr} - 🎶${trackTitle}` : titleStr}</title>
      <meta name="title" content={title} />
      <meta name="description" content={content} />
      <meta name="apple-mobile-web-app-title" content={content} />

      {route && <link rel="canonical" href={`${baseUrl}${accessRoutes[lang][route]}${canonicalSuffix}${queryStr}`} />}
      {route && showHrefLangFr && <link rel="alternate" hreflang="fr" href={`${baseUrl}${accessRoutes["fr"][route]}${suffixFr}`} />}
      {route && showHrefLangEn && <link rel="alternate" hreflang="en" href={`${baseUrl}${accessRoutes["en"][route]}${suffix}`} />}
      {route && <link rel="alternate" href={`${baseUrl}/en${xDefault ? "/" + xDefault : ""}`} hreflang="x-default" />}

      {prerenderStatusCode && <meta name="prerender-status-code" content={prerenderStatusCode}></meta>}
      
      {noIndex && <meta name="robots" content="noindex" />}
      {noIndex && <meta name="googlebot" content="noindex" />}
    </Helmet>
  );
};

const mapStateToProps = (state) => ({
  lang: getLangSelector(state),
  miniPlayer: getMiniPlayerSelector(state),
});

export default withTranslation()(connect(mapStateToProps)(VialmaHelmet));
